import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import axios from "axios";
import { dateFormat, generateSubmittedBatchPDF } from "../../utils";
import { BILLING_SUBMISSION_DETAILS } from "../../constant";
import Swal from "../../messageBox/customSweetAlert";

const PRIMARY_COLOR = "#F4A442";
const SECONDARY_COLOR = "#2D5A50";

function SubmittedBatchTable({
  tableData,
  setTabelData,
  questionList,
  pagination = {},
  onPageChange,
}) {
  const [exportingBatchId, setExportingBatchId] = useState(null);
  const {
    currentPage = 1,
    totalPages = 1,
    totalRecords = 0,
    recordsPerPage = 10,
  } = pagination;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(
    startIndex + (tableData?.length || 0),
    totalRecords,
  );
  const handlePage = onPageChange || (() => {});

  const getPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "ellipsis", totalPages];
  };

  const pageNumbers = getPageNumbers();

  const handleExportClick = async (batchRow) => {
    const batchId = batchRow?.id;
    if (batchId == null || exportingBatchId != null) return;

    setExportingBatchId(batchId);
    try {
      const response = await axios.get(`${BILLING_SUBMISSION_DETAILS}/${batchId}`);
      const root = response?.data ?? {};
      const isOk =
        root?.success === true ||
        root?.status === 200 ||
        root?.status === "200";

      const batchData = root?.data;
      if (!isOk || !Array.isArray(batchData) || batchData.length === 0) {
        Swal.fire({
          title: "Failed!",
          text:
            root?.message ||
            "No batch details found to generate the export PDF.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      generateSubmittedBatchPDF(batchData, {}, `batch-${batchId}.pdf`);

      Swal.fire({
        title: "Success!",
        text: root?.message || "Batch PDF exported successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to export batch PDF.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setExportingBatchId(null);
    }
  };

  return (
    <>
      <Table striped bordered hover className="tbl-require">
        <thead>
          <tr>
            <th>#</th>
            <th>Batch Name</th>
            <th>Date Of Submission</th>
            <th>Total Amount</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {tableData &&
            tableData.map((details, index) => (
              <tr key={details?.id}>
                <td>{startIndex + index + 1}</td>
                <td>{details.batch_name}</td>
                <td>{dateFormat(details.date_submission)}</td>
                <td>{details?.total_amount ?? "N/A"}</td>
                <td>
                  {details?.submission_status === 1 ? "Active" : "Inactive"}
                </td>
                <td style={{ textAlign: "center", cursor: "pointer" }}>
                  {exportingBatchId === details?.id ? (
                    <i
                      className="pi pi-spin pi-spinner"
                      title="export"
                      style={{ fontSize: "18px", color: "#31876f" }}
                    />
                  ) : (
                    <i
                      className="pi pi-download"
                      title="export"
                      style={{
                        fontSize: "18px",
                        color: "#31876f",
                        opacity: exportingBatchId != null ? 0.5 : 1,
                        pointerEvents:
                          exportingBatchId != null ? "none" : "auto",
                      }}
                      onClick={() => handleExportClick(details)}
                    />
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div
          className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 px-4 py-3 mt-0"
          style={{
            backgroundColor: "rgba(248, 249, 250, 0.8)",
            border: "1px solid #e9ecef",
            borderTop: "none",
            borderRadius: "0 0 0.25rem 0.25rem",
          }}
        >
          <p className="text-sm text-muted mb-0">
            Showing{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {tableData?.length ? startIndex + 1 : 0}-{endIndex}
            </span>{" "}
            of{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {totalRecords}
            </span>{" "}
            results
          </p>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: currentPage === 1 ? 0.35 : 1 }}
              disabled={currentPage === 1}
              onClick={() => handlePage(1)}
              title="First page"
              aria-label="First page"
            >
              <i className="pi pi-angle-double-left" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none me-1"
              style={{ opacity: currentPage === 1 ? 0.35 : 1 }}
              disabled={currentPage === 1}
              onClick={() => handlePage(currentPage - 1)}
              title="Previous page"
              aria-label="Previous page"
            >
              <i className="pi pi-angle-left" />
            </button>
            <div className="d-flex align-items-center gap-1">
              {pageNumbers.map((p, i) =>
                p === "ellipsis" ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-muted">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className="btn btn-sm fw-semibold rounded"
                    style={{
                      minWidth: 32,
                      height: 32,
                      backgroundColor:
                        p === currentPage ? PRIMARY_COLOR : "transparent",
                      color: p === currentPage ? "#fff" : "#495057",
                      border: "none",
                      boxShadow:
                        p === currentPage
                          ? "0 1px 2px rgba(0,0,0,0.05)"
                          : "none",
                    }}
                    onClick={() => handlePage(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === currentPage ? "page" : undefined}
                  >
                    {p}
                  </button>
                ),
              )}
            </div>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none ms-1"
              style={{ opacity: currentPage === totalPages ? 0.35 : 1 }}
              disabled={currentPage === totalPages}
              onClick={() => handlePage(currentPage + 1)}
              title="Next page"
              aria-label="Next page"
            >
              <i className="pi pi-angle-right" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: currentPage === totalPages ? 0.35 : 1 }}
              disabled={currentPage === totalPages}
              onClick={() => handlePage(totalPages)}
              title="Last page"
              aria-label="Last page"
            >
              <i className="pi pi-angle-double-right" />
            </button>
          </div>
        </div>
      )}
      {totalRecords > 0 && totalPages <= 1 && (
        <div className="small text-muted mt-2">
          Total records: {totalRecords}
        </div>
      )}
    </>
  );
}

export default SubmittedBatchTable;
